import formatProviderName from '@utils/formatProviderName';
import { ProviderRole } from '@enums/ProviderRole';
import { CurrentPlanRanking } from '@enums/CurrentPlanRanking';
import { titleCase } from 'title-case';
import { getDrxDeepLink } from '@utils/config';
import { PlanType } from '@enums/PlanType';

type InNetworkCoverage = {
  providers: [any];
  prescriptions: [any];
};

export enum FilterType {
  prescriptions = 'prescriptions',
  specialistProviders = 'specialistProviders',
  primaryProviders = 'primaryProviders',
}

export type Filter = {
  name?: string;
  value: string;
  type: FilterType;
  checked: boolean;
  inNetworkPercentage?: number;
};

export enum ContentState {
  Loading = 'loading',
  NoPlans = 'noPlans',
  Optimal = 'optimal',
  Pareto = 'pareto',
  SwitchRecommended = 'switchRecommended',
  drugsNotCovered = 'drugsNotCovered',
  PrecallSurvey = 'precallSurvey',
  NoCurrentPlan = 'noCurrentPlan',
}

interface GetContentStateArgs {
  recommendations: any[];
  currentPlanRanking?: CurrentPlanRanking;
  currentPlanDrugsCovered?: boolean;
  allProvidersInNetwork?: boolean;
  topSavings?: number;
  loadingPlans?: boolean;
  loadedPlans?: boolean;
  drugsNotCovered?: any[];
  planType: PlanType;
  currentPlanNotFound?: boolean;
}

/*
 * Get inNetworkPercentage for a provider.
 */
const getInNetworkPercentageForProvider = (provider: any, inNetworkCoverage: InNetworkCoverage) => {
  return inNetworkCoverage?.providers.find((iterProvider: any) => iterProvider.id === provider.id)?.inNetworkPercentage;
};

/*
 * Get inNetworkPercentage for a prescription.
 */
export const getInNetworkPercentageForPrescription = (prescription: any, inNetworkCoverage: InNetworkCoverage) => {
  return inNetworkCoverage?.prescriptions.find((iterPrescription: any) => iterPrescription.id === prescription.id)
    ?.inNetworkPercentage;
};

/*
 * Create filters object from initial user data.
 */
export const createFilters = (prescriptions: [any], providers: [any], inNetworkCoverage?: InNetworkCoverage) => {
  const filters: {
    primaries: Filter[];
    specialists: Filter[];
    prescriptions: Filter[];
  } = {
    primaries: [],
    prescriptions: [],
    specialists: [],
  };

  filters.prescriptions = prescriptions.map((prescription: any) => ({
    type: FilterType.prescriptions,
    name: titleCase(prescription.label_name.toLowerCase()),
    value: prescription.id,
    checked: !!prescription.lapro_active,
    inNetworkPercentage: getInNetworkPercentageForPrescription(prescription, inNetworkCoverage),
  }));

  filters.primaries = providers
    .filter((provider: any) => provider.provider_role === ProviderRole.PRIMARY)
    .map((provider: any) => {
      return {
        type: FilterType.primaryProviders,
        name: formatProviderName(provider),
        value: provider.id,
        checked: !!provider.lapro_active,
        inNetworkPercentage: getInNetworkPercentageForProvider(provider, inNetworkCoverage),
      };
    });

  // Some providers have provider_role = NULL, let's add those
  // to the list of specialists.
  filters.specialists = providers
    .filter(
      (provider: any) =>
        provider.provider_role === ProviderRole.SPECIALIST ||
        provider.provider_role === ProviderRole.HOSPITAL ||
        !provider.provider_role
    )
    .map((provider: any) => ({
      type: FilterType.specialistProviders,
      name: formatProviderName(provider),
      value: provider.id,
      checked: !!provider.lapro_active,
      inNetworkPercentage: getInNetworkPercentageForProvider(provider, inNetworkCoverage),
    }));

  return filters;
};

export const getRelayState = (
  year: number,
  recommendations,
  zip: string,
  fips: string,
  currentPlanId: string = null
) => {
  const base = getDrxDeepLink(year);
  if (!recommendations.length) return `${base}/Shopping/Home`;

  // sort by savings
  recommendations.sort((a, b) => b.savings - a.savings);
  const planIds = recommendations.map((recommendation) => recommendation.id);

  // If a current plan is provided, add cms id to plan details.
  if (currentPlanId && planIds.length) planIds.unshift(currentPlanId);

  // Dedupe list of planIds.
  const unique = planIds.reduce((acc, current) => {
    return acc.includes(current) ? acc : [...acc, current];
  }, []);

  // Connecture Plan Compare only accepts three plan Ids.
  const firstThreeIds = unique.slice(0, 3);

  return firstThreeIds.length === 1
    ? `${base}/Shopping/PlanDetails?ZipCode=${zip}&CountyFIPS=${fips}&PlanID=${firstThreeIds}`
    : `${base}/Shopping/ComparePlans?ZipCode=${zip}&CountyFIPS=${fips}&PlanID=${firstThreeIds}`;
};

export const getPlanTypeCopy = (planType: PlanType) => {
  const copy = {
    [PlanType.MEDIGAP]: 'drugs',
    [PlanType.MAPD]: 'drugs and doctors',
    [PlanType.PDP]: 'drugs',
    [PlanType.MA]: 'doctors',
  };
  return copy[planType];
};

// I have a degree in computer science
export const formatNumber = (num: number) => {
  if (num === 0) return 'zero';
  if (num === 1) return 'one';
  if (num === 2) return 'two';
  if (num === 3) return 'three';
  if (num === 4) return 'four';
  if (num === 5) return 'five';
  if (num === 6) return 'six';
  if (num === 7) return 'seven';
  if (num === 8) return 'eight';
  if (num === 9) return 'nine';
  if (num === 10) return 'ten';
  return num;
};

// If the top recommended plan has a saving below this amount, we consider this case to be Optimal (no switch).
export const SAVINGS_THRESHOLD = 200; // USD

export const getContentState = ({
  currentPlanNotFound,
  allProvidersInNetwork,
  currentPlanDrugsCovered,
  currentPlanRanking,
  loadedPlans,
  loadingPlans,
  recommendations,
  topSavings,
  drugsNotCovered,
  planType,
}: GetContentStateArgs): ContentState | null => {
  if (loadingPlans) return ContentState.Loading;

  if (currentPlanNotFound) {
    return ContentState.NoCurrentPlan;
  }

  if (loadedPlans) {
    // No plans found!
    if (!recommendations.length) return ContentState.NoCurrentPlan;

    if (drugsNotCovered.length) return ContentState.drugsNotCovered;
    // Optimal case 1.
    if (currentPlanRanking === CurrentPlanRanking.TOP_PLAN_IN_REC) return ContentState.Optimal;

    // if recommended plans are all negative savings then it is optimal.
    if (recommendations.every((rec) => rec.savings < 0)) return ContentState.Optimal;

    // Optimal case 2.
    if (
      (currentPlanRanking === CurrentPlanRanking.IN_REC || currentPlanRanking === CurrentPlanRanking.NOT_IN_REC) &&
      topSavings <= 0 &&
      currentPlanDrugsCovered &&
      (planType === PlanType.PDP ? true : allProvidersInNetwork)
    ) {
      return ContentState.Optimal;
    }
    // Pareto case 1
    if (
      (currentPlanRanking === CurrentPlanRanking.IN_REC || currentPlanRanking === CurrentPlanRanking.NOT_IN_REC) &&
      topSavings > 0 &&
      topSavings < SAVINGS_THRESHOLD
    ) {
      return ContentState.Pareto;
    }

    // Switch cases.
    if (recommendations.length) return ContentState.SwitchRecommended;
  }

  return null;
};

/*
 * Create a comprehensive filter persistence object for localStorage.
 * This preserves badge data for all filter types when items become inactive.
 */
export const createFilterPersistenceData = (filters: {
  primaries: Filter[];
  specialists: Filter[];
  prescriptions: Filter[];
}) => {
  const persistenceData: {
    prescriptions: { [key: string]: number | undefined };
    providers: { [key: string]: number | undefined };
  } = {
    prescriptions: {},
    providers: {},
  };

  // Store prescription data
  filters.prescriptions.forEach((filter) => {
    persistenceData.prescriptions[filter.value] = filter.inNetworkPercentage;
  });

  // Store provider data (both primary and specialist)
  [...filters.primaries, ...filters.specialists].forEach((filter) => {
    persistenceData.providers[filter.value] = filter.inNetworkPercentage;
  });

  return persistenceData;
};

/*
 * Restore filter data from localStorage persistence.
 */
export const restoreFilterData = (
  newFilters: {
    primaries: Filter[];
    specialists: Filter[];
    prescriptions: Filter[];
  },
  persistenceData: any
) => {
  if (!persistenceData) return newFilters;

  // Restore prescription data
  if (persistenceData.prescriptions) {
    Object.keys(persistenceData.prescriptions).forEach((id) => {
      const matchingFilter = newFilters.prescriptions.find((filter) => filter.value === id);
      if (matchingFilter && matchingFilter.inNetworkPercentage === undefined) {
        matchingFilter.inNetworkPercentage = persistenceData.prescriptions[id];
      }
    });
  }

  // Restore provider data
  if (persistenceData.providers) {
    Object.keys(persistenceData.providers).forEach((id) => {
      // Check primary providers
      let matchingFilter = newFilters.primaries.find((filter) => filter.value === id);
      if (matchingFilter && matchingFilter.inNetworkPercentage === undefined) {
        matchingFilter.inNetworkPercentage = persistenceData.providers[id];
      }

      // Check specialist providers
      matchingFilter = newFilters.specialists.find((filter) => filter.value === id);
      if (matchingFilter && matchingFilter.inNetworkPercentage === undefined) {
        matchingFilter.inNetworkPercentage = persistenceData.providers[id];
      }
    });
  }

  return newFilters;
};
